import { Controller } from "@hotwired/stimulus"

/**
 * Handles bulk selection for tables with checkboxes.
 * Manages "select all" checkbox, tracks selection count, and shows/hides bulk action button.
 * Supports optional "select all matching" banner for paginated results.
 *
 * Usage:
 *   <table data-controller="bulk-select"
 *          data-bulk-select-button-id-value="bulk-generate-btn"
 *          data-bulk-select-count-id-value="bulk-count">
 *     <thead>
 *       <tr>
 *         <th><input type="checkbox" data-action="change->bulk-select#toggleAll" data-bulk-select-target="selectAll"></th>
 *       </tr>
 *     </thead>
 *     <tbody>
 *       <tr>
 *         <td><input type="checkbox" name="ids[]" value="1" data-action="change->bulk-select#updateCount" data-bulk-select-target="checkbox"></td>
 *       </tr>
 *     </tbody>
 *   </table>
 *
 *   <button id="bulk-generate-btn" class="hidden">
 *     Generate (<span id="bulk-count">0</span>)
 *   </button>
 */
export default class BulkSelectController extends Controller {
  static targets = ["selectAll", "checkbox", "banner"]
  static values = {
    buttonId: String,
    countId: String,
    totalCount: Number,
    pageCount: Number,
    bannerLabel: { type: String, default: "items" },
  }

  declare selectAllTarget: HTMLInputElement
  declare checkboxTargets: HTMLInputElement[]
  declare hasSelectAllTarget: boolean
  declare bannerTarget: HTMLElement
  declare hasBannerTarget: boolean
  declare buttonIdValue: string
  declare countIdValue: string
  declare totalCountValue: number
  declare pageCountValue: number
  declare bannerLabelValue: string

  private selectAllMatching = false

  connect() {
    this.updateCount()
  }

  toggleAll(event: Event): void {
    const checked = (event.target as HTMLInputElement).checked
    this.checkboxTargets.forEach((checkbox) => {
      checkbox.checked = checked
    })
    this.selectAllMatching = false
    this.updateBanner()
    this.updateCount()
  }

  selectAllMatchingAction(): void {
    this.selectAllMatching = true
    this.updateBanner()
    this.updateCount()
  }

  deselectAllMatching(): void {
    this.selectAllMatching = false
    this.updateBanner()
    this.updateCount()
  }

  toggle(): void {
    if (this.selectAllMatching) {
      this.selectAllMatching = false
    }
    this.updateBanner()
    this.updateCount()
  }

  updateCount(): void {
    const checkedCount = this.selectAllMatching
      ? this.totalCountValue
      : this.checkboxTargets.filter((cb) => cb.checked).length
    const totalOnPage = this.checkboxTargets.length

    if (this.countIdValue) {
      const countEl = document.getElementById(this.countIdValue)
      if (countEl) {
        countEl.textContent = String(checkedCount)
      }
    }

    if (this.hasSelectAllTarget) {
      if (!this.selectAllMatching) {
        this.selectAllTarget.checked =
          checkedCount === totalOnPage && totalOnPage > 0
        this.selectAllTarget.indeterminate =
          checkedCount > 0 && checkedCount < totalOnPage
      } else {
        this.selectAllTarget.checked = true
        this.selectAllTarget.indeterminate = false
      }
    }

    if (this.buttonIdValue) {
      const btn = document.getElementById(this.buttonIdValue)
      if (btn) {
        if (checkedCount > 0) {
          btn.classList.remove("hidden")
          btn.classList.add("flex")
          btn.removeAttribute("disabled")
        } else {
          btn.classList.add("hidden")
          btn.classList.remove("flex")
          btn.setAttribute("disabled", "disabled")
        }
      }
    }
  }

  private updateBanner(): void {
    if (!this.hasBannerTarget) return

    const allChecked =
      this.checkboxTargets.length > 0 &&
      this.checkboxTargets.every((cb) => cb.checked)
    const hasMorePages = this.totalCountValue > this.pageCountValue

    const label = this.bannerLabelValue

    if (this.selectAllMatching) {
      this.bannerTarget.classList.remove("hidden")
      this.bannerTarget.classList.add("flex")
      this.bannerTarget.innerHTML = this.buildBannerHTML(
        `All ${this.totalCountValue} matching ${label} are selected.`,
        "Deselect all",
        "deselectAllMatching"
      )
    } else if (allChecked && hasMorePages) {
      this.bannerTarget.classList.remove("hidden")
      this.bannerTarget.classList.add("flex")
      this.bannerTarget.innerHTML = this.buildBannerHTML(
        `All ${this.pageCountValue} ${label} on this page are selected.`,
        `Select all ${this.totalCountValue} matching ${label}`,
        "selectAllMatchingAction"
      )
    } else {
      this.bannerTarget.classList.add("hidden")
      this.bannerTarget.classList.remove("flex")
    }

    const hiddenField = this.element.querySelector<HTMLInputElement>(
      'input[name="select_all_matching"]'
    )
    if (hiddenField) {
      hiddenField.value = this.selectAllMatching ? "true" : "false"
    }
  }

  private buildBannerHTML(
    message: string,
    actionText: string,
    actionMethod: string
  ): string {
    return `
    <span>${message}</span>
    <button type="button"
      class="link link-primary font-medium"
      data-action="bulk-select#${actionMethod}">
      ${actionText}
    </button>
  `
  }
}
